import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Plus, ChevronUp, ChevronDown, Trash2, Edit, FileSignature, ClipboardList, 
  ExternalLink, Link2, Eye, Copy, DollarSign, CheckCircle, X, Loader2, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: '📝' },
  { value: 'textarea', label: 'Long Text', icon: '📄' },
  { value: 'email', label: 'Email', icon: '📧' },
  { value: 'phone', label: 'Phone', icon: '📱' },
  { value: 'number', label: 'Number', icon: '🔢' },
  { value: 'date', label: 'Date', icon: '📅' },
  { value: 'select', label: 'Dropdown', icon: '📋' },
  { value: 'checkbox', label: 'Checkbox', icon: '☑️' },
  { value: 'radio', label: 'Multiple Choice', icon: '🔘' },
  { value: 'file', label: 'File Upload', icon: '📎' },
  { value: 'signature', label: 'Signature', icon: '✍️' },
  { value: 'address', label: 'Address', icon: '🏠' },
  { value: 'payment', label: 'Payment Amount', icon: '💳' },
];

const DEFAULT_TEMPLATE = {
  form_type: 'adoption',
  title: '',
  description: '',
  require_signature: true,
  header_text: '',
  footer_text: '',
  terms_and_conditions: '',
  fields: [],
  is_published: false,
  payment_enabled: false,
  payment_amount: 0,
  payment_description: '',
  stripe_payment_link: ''
};

// Form Preview Component
const FormPreview = ({ formData, onClose }) => {
  const renderPreviewField = (field) => {
    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'number':
        return <Input type={field.type === 'phone' ? 'tel' : field.type} placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} disabled />;
      case 'textarea':
        return <Textarea placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`} rows={3} disabled />;
      case 'date':
        return <Input type="date" disabled />;
      case 'select':
        return (
          <Select disabled>
            <SelectTrigger><SelectValue placeholder="Select an option" /></SelectTrigger>
          </Select>
        );
      case 'radio':
        return (
          <RadioGroup disabled>
            {field.options?.map((opt, idx) => (
              <div key={idx} className="flex items-center space-x-2">
                <RadioGroupItem value={opt} id={`preview_${field.id}_${idx}`} disabled />
                <Label htmlFor={`preview_${field.id}_${idx}`}>{opt}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox disabled />
            <Label>{field.placeholder || 'Yes'}</Label>
          </div>
        );
      case 'file':
        return <Input type="file" disabled />;
      case 'address':
        return <Textarea placeholder="Enter full address" rows={2} disabled />;
      case 'signature':
        return (
          <div className="border-2 border-dashed rounded-lg p-8 text-center text-stone-400">
            <FileSignature className="w-8 h-8 mx-auto mb-2" />
            <p className="text-sm">Digital signature field</p>
          </div>
        );
      case 'payment':
        return (
          <div className="p-4 bg-green-50 rounded-lg">
            <p className="font-medium text-green-800">Payment Amount: ${formData.payment_amount || 0}</p>
            <p className="text-sm text-green-600">{formData.payment_description || 'Payment required'}</p>
          </div>
        );
      default:
        return <Input placeholder={field.placeholder} disabled />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Preview Header */}
        <div className="sticky top-0 bg-orange-500 text-white p-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            <span className="font-semibold">Form Preview (Draft)</span>
          </div>
          <Button size="sm" variant="ghost" className="text-white hover:bg-white/20" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Form Content */}
        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-stone-800">{formData.title || 'Untitled Form'}</h1>
            {formData.description && (
              <p className="text-stone-600 mt-2">{formData.description}</p>
            )}
          </div>

          {/* Header Text */}
          {formData.header_text && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6">
              <p className="text-stone-700 whitespace-pre-wrap">{formData.header_text}</p>
            </div>
          )}

          {/* Form Fields */}
          <div className="space-y-6">
            {formData.fields?.length > 0 ? (
              formData.fields.map((field) => (
                <div key={field.id} className="space-y-2">
                  <Label className="flex items-center gap-1">
                    {field.label}
                    {field.required && <span className="text-red-500">*</span>}
                  </Label>
                  {renderPreviewField(field)}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-stone-400">
                <p>No fields added yet</p>
                <p className="text-sm">Add fields to see them in the preview</p>
              </div>
            )}
          </div>

          {/* Terms and Conditions */}
          {formData.terms_and_conditions && (
            <div className="mt-6 pt-6 border-t">
              <div className="bg-stone-50 p-4 rounded-lg mb-4 max-h-32 overflow-y-auto">
                <p className="text-sm text-stone-600 whitespace-pre-wrap">{formData.terms_and_conditions}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox disabled />
                <Label className="text-sm">I have read and agree to the terms and conditions</Label>
              </div>
            </div>
          )}

          {/* Signature Notice */}
          {formData.require_signature && (
            <div className="mt-6 flex items-center gap-2 text-sm text-stone-500 bg-stone-50 p-3 rounded-lg">
              <FileSignature className="w-5 h-5" />
              <span>By submitting this form, you are providing your digital signature.</span>
            </div>
          )}

          {/* Footer Text */}
          {formData.footer_text && (
            <p className="mt-6 text-center text-sm text-stone-500">{formData.footer_text}</p>
          )}

          {/* Submit Button (disabled in preview) */}
          <Button className="w-full mt-6" size="lg" disabled>
            Submit Form (Preview Only)
          </Button>
        </div>
      </div>
    </div>
  );
};

const FormBuilder = ({ formTemplates, onUpdate, websiteUrl }) => {
  // Use prop directly instead of local state to avoid effect warning
  const templates = formTemplates || [];
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_TEMPLATE });
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(null);

  const generatePaymentLink = async (template) => {
    if (!template.payment_enabled || template.payment_amount <= 0) {
      toast.error('Please enable payment and set an amount first');
      return;
    }
    
    setGeneratingPaymentLink(template.template_id);
    try {
      const response = await axios.post(`/shelter/form-templates/${template.template_id}/generate-payment-link`);
      toast.success('Payment link generated successfully!');
      navigator.clipboard.writeText(response.data.payment_link);
      toast.info('Payment link copied to clipboard');
      onUpdate?.();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to generate payment link';
      toast.error(errorMsg);
    } finally {
      setGeneratingPaymentLink(null);
    }
  };

  const moveField = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= formData.fields.length) return;
    
    const items = Array.from(formData.fields);
    const [movedItem] = items.splice(index, 1);
    items.splice(newIndex, 0, movedItem);
    
    setFormData(prev => ({ ...prev, fields: items }));
  };

  const addField = (fieldType) => {
    const newField = {
      id: `field_${uuidv4()}`,
      type: fieldType,
      label: FIELD_TYPES.find(f => f.value === fieldType)?.label || 'New Field',
      required: false,
      placeholder: '',
      options: fieldType === 'select' || fieldType === 'radio' ? ['Option 1', 'Option 2'] : [],
      validation: {}
    };
    setFormData(prev => ({ ...prev, fields: [...prev.fields, newField] }));
  };

  const updateField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const removeField = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const handleCreate = async () => {
    if (!formData.title) {
      toast.error('Please enter a form title');
      return;
    }
    
    try {
      await axios.post('/shelter/form-templates', formData);
      toast.success('Form template created!');
      setShowCreateDialog(false);
      setFormData({ ...DEFAULT_TEMPLATE });
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to create form template');
    }
  };

  const handleEdit = async () => {
    if (!formData.title) {
      toast.error('Please enter a form title');
      return;
    }
    
    try {
      await axios.put(`/shelter/form-templates/${editingTemplate.template_id}`, formData);
      toast.success('Form template updated!');
      setShowEditDialog(false);
      setEditingTemplate(null);
      setFormData({ ...DEFAULT_TEMPLATE });
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update form template');
    }
  };

  const togglePublish = async (template) => {
    try {
      await axios.put(`/shelter/form-templates/${template.template_id}/publish`, {
        is_published: !template.is_published
      });
      if (!template.is_published) {
        toast.success('Form published! It will now appear on your website\'s Forms page.');
      } else {
        toast.success('Form unpublished and removed from website.');
      }
      onUpdate?.();
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const copyFormLink = (template) => {
    const link = `${websiteUrl || window.location.origin}/forms/${template.template_id}`;
    navigator.clipboard.writeText(link);
    toast.success('Form link copied to clipboard!');
  };

  const openEditDialog = (template) => {
    setEditingTemplate(template);
    setFormData({
      form_type: template.form_type || 'adoption',
      title: template.title || '',
      description: template.description || '',
      require_signature: template.require_signature ?? true,
      header_text: template.header_text || '',
      footer_text: template.footer_text || '',
      terms_and_conditions: template.terms_and_conditions || '',
      fields: template.fields || [],
      is_published: template.is_published || false,
      payment_enabled: template.payment_enabled || false,
      payment_amount: template.payment_amount || 0,
      payment_description: template.payment_description || '',
      stripe_payment_link: template.stripe_payment_link || ''
    });
    setShowEditDialog(true);
  };

  const openPreview = (template) => {
    setPreviewData({
      ...template,
      form_type: template.form_type || 'adoption',
      title: template.title || '',
      description: template.description || '',
      fields: template.fields || [],
    });
    setShowPreview(true);
  };

  const previewCurrentForm = () => {
    setPreviewData(formData);
    setShowPreview(true);
  };

  const resetFormData = () => {
    setFormData({ ...DEFAULT_TEMPLATE });
  };

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const renderDialogContent = (isEdit) => (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center justify-between">
          <span>{isEdit ? 'Edit Form Template' : 'Create Form Template'}</span>
          <Button size="sm" variant="outline" onClick={previewCurrentForm}>
            <Eye className="w-4 h-4 mr-2" />Preview
          </Button>
        </DialogTitle>
      </DialogHeader>
      
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Form Settings */}
        <div className="space-y-4">
          <div>
            <Label>Form Type</Label>
            <Select value={formData.form_type} onValueChange={(v) => handleInputChange('form_type', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="adoption">Adoption Application</SelectItem>
                <SelectItem value="foster">Foster Application</SelectItem>
                <SelectItem value="volunteer">Volunteer Application</SelectItem>
                <SelectItem value="surrender">Surrender Form</SelectItem>
                <SelectItem value="donation">Donation Form</SelectItem>
                <SelectItem value="contact">Contact Form</SelectItem>
                <SelectItem value="event">Event Registration</SelectItem>
                <SelectItem value="custom">Custom Form</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label>Form Title *</Label>
            <Input 
              value={formData.title} 
              onChange={(e) => handleInputChange('title', e.target.value)} 
              placeholder="e.g., Dog Adoption Application" 
            />
          </div>
          
          <div>
            <Label>Description</Label>
            <Textarea 
              value={formData.description} 
              onChange={(e) => handleInputChange('description', e.target.value)} 
              placeholder="Brief description" 
              rows={2}
            />
          </div>
          
          <div>
            <Label>Header Text</Label>
            <Textarea 
              value={formData.header_text} 
              onChange={(e) => handleInputChange('header_text', e.target.value)} 
              placeholder="Displayed at top of form" 
              rows={2}
            />
          </div>
          
          <div>
            <Label>Terms & Conditions</Label>
            <Textarea 
              value={formData.terms_and_conditions} 
              onChange={(e) => handleInputChange('terms_and_conditions', e.target.value)} 
              placeholder="Legal terms to be agreed to" 
              rows={3}
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={formData.require_signature} 
              onCheckedChange={(c) => handleInputChange('require_signature', c)} 
            />
            <Label>Require Digital Signature</Label>
          </div>

          {/* Payment Settings */}
          <Card className="border-2 border-dashed">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> Payment Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2">
                <Checkbox 
                  checked={formData.payment_enabled} 
                  onCheckedChange={(c) => handleInputChange('payment_enabled', c)} 
                />
                <Label>Enable Payment Collection</Label>
              </div>
              
              {formData.payment_enabled && (
                <>
                  <div>
                    <Label>Payment Amount ($)</Label>
                    <Input 
                      type="number" 
                      value={formData.payment_amount} 
                      onChange={(e) => handleInputChange('payment_amount', parseFloat(e.target.value) || 0)} 
                      placeholder="0.00" 
                    />
                  </div>
                  <div>
                    <Label>Payment Description</Label>
                    <Input 
                      value={formData.payment_description} 
                      onChange={(e) => handleInputChange('payment_description', e.target.value)} 
                      placeholder="e.g., Adoption fee" 
                    />
                  </div>
                  <div>
                    <Label>Stripe Payment Link</Label>
                    <div className="flex gap-2">
                      <Input 
                        value={formData.stripe_payment_link} 
                        onChange={(e) => handleInputChange('stripe_payment_link', e.target.value)} 
                        placeholder="https://buy.stripe.com/..." 
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-stone-500 mt-1">
                      Paste your Stripe Payment Link or generate one automatically after saving the form.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Form Fields */}
        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Add Fields</Label>
            <div className="flex flex-wrap gap-1">
              {FIELD_TYPES.map((type) => (
                <Button
                  key={type.value}
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => addField(type.value)}
                >
                  {type.icon} {type.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="border rounded-lg p-3 min-h-[300px] bg-stone-50">
            <Label className="mb-2 block text-sm">Form Fields (use arrows to reorder)</Label>
            
            <div className="space-y-2">
              {formData.fields.map((field, index) => (
                <div
                  key={field.id}
                  className="bg-white border rounded-lg p-3"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {/* Move Up/Down Buttons */}
                    <div className="flex flex-col">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0"
                        onClick={() => moveField(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-5 w-5 p-0"
                        onClick={() => moveField(index, 'down')}
                        disabled={index === formData.fields.length - 1}
                      >
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </div>
                    
                    <Badge variant="secondary" className="text-xs">
                      {FIELD_TYPES.find(t => t.value === field.type)?.icon} {field.type}
                    </Badge>
                    <div className="flex-1">
                      <Input
                        value={field.label}
                        onChange={(e) => updateField(field.id, { label: e.target.value })}
                        className="h-7 text-sm"
                        placeholder="Field label"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <Checkbox
                        checked={field.required}
                        onCheckedChange={(c) => updateField(field.id, { required: c })}
                      />
                      <span className="text-xs">Req</span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-red-500"
                      onClick={() => removeField(field.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {(field.type === 'select' || field.type === 'radio') && (
                    <div className="ml-10">
                      <Label className="text-xs">Options (comma separated)</Label>
                      <Input
                        value={field.options?.join(', ') || ''}
                        onChange={(e) => updateField(field.id, { options: e.target.value.split(',').map(o => o.trim()) })}
                        className="h-7 text-xs"
                        placeholder="Option 1, Option 2, Option 3"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {formData.fields.length === 0 && (
              <p className="text-center text-stone-400 py-8 text-sm">
                Click the field buttons above to add form fields
              </p>
            )}
          </div>
        </div>
      </div>
      
      <DialogFooter>
        <Button variant="outline" onClick={previewCurrentForm}>
          <Eye className="w-4 h-4 mr-2" />Preview Form
        </Button>
        <Button variant="outline" onClick={() => isEdit ? setShowEditDialog(false) : setShowCreateDialog(false)}>
          Cancel
        </Button>
        <Button onClick={isEdit ? handleEdit : handleCreate}>
          {isEdit ? 'Save Changes' : 'Create Form'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Forms & Digital Signatures</h2>
        <Button onClick={() => { resetFormData(); setShowCreateDialog(true); }} className="bg-primary hover:bg-primary/90">
          <Plus className="w-4 h-4 mr-2" />Create Form Template
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Form Templates */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSignature className="w-5 h-5" />Form Templates
            </CardTitle>
            <CardDescription>Create and manage your form templates</CardDescription>
          </CardHeader>
          <CardContent>
            {templates.length > 0 ? (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div key={template.template_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-stone-50">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium">{template.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="secondary" className="text-xs">{template.form_type}</Badge>
                          {template.is_published ? (
                            <Badge className="bg-green-100 text-green-800 text-xs">Published</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Draft</Badge>
                          )}
                          {template.payment_enabled && (
                            <Badge className="bg-blue-100 text-blue-800 text-xs flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />${template.payment_amount}
                            </Badge>
                          )}
                          {template.fields?.length > 0 && (
                            <span className="text-xs text-stone-500">{template.fields.length} fields</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openPreview(template)} title="Preview">
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEditDialog(template)} title="Edit">
                        <Edit className="w-4 h-4" />
                      </Button>
                      {template.payment_enabled && template.payment_amount > 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className={`h-8 w-8 p-0 ${template.stripe_payment_link ? 'text-blue-600' : 'text-stone-400'}`}
                          onClick={() => generatePaymentLink(template)}
                          disabled={generatingPaymentLink === template.template_id}
                          title={template.stripe_payment_link ? 'Regenerate Payment Link' : 'Generate Payment Link'}
                        >
                          {generatingPaymentLink === template.template_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <CreditCard className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className={`h-8 w-8 p-0 ${template.is_published ? 'text-green-600' : 'text-stone-400'}`}
                        onClick={() => togglePublish(template)}
                        title={template.is_published ? 'Unpublish' : 'Publish'}
                      >
                        <CheckCircle className="w-4 h-4" />
                      </Button>
                      {template.is_published && (
                        <>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-8 w-8 p-0" 
                            onClick={() => window.open(`/forms/${template.template_id}`, '_blank')}
                            title="View Live Form"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => copyFormLink(template)} title="Copy Link">
                            <Copy className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-4">No form templates yet. Create your first form!</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Link2 className="w-5 h-5" />Published Form Links
            </CardTitle>
            <CardDescription>Direct links to your live forms</CardDescription>
          </CardHeader>
          <CardContent>
            {templates.filter(t => t.is_published).length > 0 ? (
              <div className="space-y-3">
                {templates.filter(t => t.is_published).map((template) => (
                  <div key={template.template_id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="font-medium">{template.title}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7"
                        onClick={() => copyFormLink(template)}
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        className="h-7 bg-green-600 hover:bg-green-700"
                        onClick={() => window.open(`/forms/${template.template_id}`, '_blank')}
                      >
                        <ExternalLink className="w-3 h-3 mr-1" /> View Live
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-stone-500 mb-2">No published forms yet</p>
                <p className="text-xs text-stone-400">Publish a form to get a shareable link</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        {renderDialogContent(false)}
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        {renderDialogContent(true)}
      </Dialog>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <FormPreview formData={previewData} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
};

export default FormBuilder;
